// ============================================================
// HABIT OS — Bibliothek: Licht-Protokolle
// Morgenlicht · Blaues Licht · Rotlicht-Therapie
// Saisonale Rhythmen · Kunstlicht-Optimierung
// ============================================================

export const LIBRARY_LIGHT_PROTOCOLS = {
  id: "light_protocols",
  label: "Licht-Protokolle",
  icon: "💡",
  subcategories: [

    // ── MORGENLICHT ──────────────────────────
    {
      id: "light_morning",
      label: "Morgenlicht-Protokoll",
      icon: "🌅",
      habits: [
        {
          id: "hab_light_morning_outside",
          label: "Morgensonnenlicht: 10–30 Min. draußen",
          time: "morning",
          icon: "☀️",
          why: "Morgensonnenlicht aktiviert spezielle Netzhaut-Ganglienzellen (intrinsisch photosensitive retinale Ganglienzellen, ipRGCs) die auf Melanopsin-Basis primär auf kurzwelliges blaues Licht (480nm) reagieren — aber auch auf die Gesamtintensität. Diese Zellen senden direkte Signale an den SCN (Suprachiasmatischen Nucleus) — die Master-Uhr des Körpers. Morgenlicht setzt: Cortisol-Peak-Timing (Energie, Fokus), Melatonin-Unterdrückung tagsüber, und programmiert Melatonin-Anstieg für den Abend 12–14 Stunden später.",
          source: "Czeisler et al., Science, 1989 · Berson et al., Science, 2002",
          dose: "10–30 Min. draußen in der ersten Stunde nach dem Aufwachen, ohne direkt in die Sonne zu schauen. Bei hohem UV-Index Sonnenschutz verwenden.",
        },
        {
          id: "hab_light_sunrise_viewing",
          label: "Sonnenaufgang draußen erleben",
          time: "morning",
          icon: "🌄",
          why: "Frühes Tageslicht ist ein natürlicher Taktgeber für den Tag-Nacht-Rhythmus.",
          source: "Huberman A., Huberman Lab Podcast, 2021",
          dose: "5–10 Min. draußen sein, ohne in die Sonne zu blicken. Tageslichtlampen nur UV-gefiltert nutzen, bei Augenerkrankungen vorher ärztlich abklären.",
        },
      ],
    },

    // ── BLAUES LICHT MANAGEMENT ──────────────
    {
      id: "light_blue_light",
      label: "Blaues Licht Management",
      icon: "📱",
      habits: [
        {
          id: "hab_light_blue_morning_good",
          label: "Blaues Licht morgens: Aktiv nutzen für Fokus",
          time: "morning",
          icon: "💻",
          why: "Morgens (erste 6–8 Stunden nach dem Aufwachen) ist blaues Licht von Bildschirmen und Kunstlicht unproblematisch und sogar nützlich: es verstärkt Cortisol und Norepinephrin und erhöht Fokus und Alertness. In dieser Phase: Bildschirme auf maximale Helligkeit, Nachtmodus ausschalten. Das Gehirn hat ein Zeitfenster in dem blaues Licht die Zirkadian-Uhr kalibriert — danach (nach Sonnenuntergang) ist es schädlich.",
          source: "Cajochen et al., Journal of Sleep Research, 2005",
          dose: "Morgens: Bildschirme hell, kein Nachtmodus nötig. Sonnenlicht ist optimal. Kunstlicht: LED-Lampen mit 5000–6500K Farbtemperatur für maximale Wachheit.",
        },
        {
          id: "hab_light_blue_evening_bad",
          label: "Blaues Licht abends: Der Melatonin-Killer",
          time: "night",
          icon: "🚫",
          why: "Nach Sonnenuntergang supprimiert blaues Licht (450–480nm) von Bildschirmen, LED-Deckenlichtern und Smartphones Melatonin-Produktion um 50% und verzögert sie um 2–3 Stunden. Das Gehirn interpretiert blaues Licht als 'Mittag-Signal'. Selbst 1–2 Stunden Bildschirmnutzung am Abend reichen für signifikante Melatonin-Suppression. Rotes Licht (>600nm) hat keinen Melatonin-Effekt.",
          source: "Gooley et al., Journal of Clinical Endocrinology & Metabolism, 2011",
          dose: "Ab 2 Std. vor dem Schlafen: Nachtmodus an (wärmstes Setting), Bildschirm-Helligkeit reduzieren. Oder: Blaulicht-Filtergläser (Amber-Gläser blockieren effektiv). Deckenlichter gegen warme Stehlampen tauschen (2700K).",
        },
        {
          id: "hab_light_blue_blockers",
          label: "Blaulicht-Filterbrillen: Wann sie wirken",
          time: "night",
          icon: "🕶️",
          why: "Klare Blaulicht-Filterbrillen ('Computer-Brillen') filtern nur 10–20% des blauen Lichts — meist unzureichend für abendliche Melatonin-Protektion. Effektiv sind Amber/Orange-getönte Gläser (blockieren 60–90% blaues Licht) oder rote Gläser (blockieren >99%). Studien zeigen: Amber-Gläser 2 Std. vor dem Schlafen erhöhen Melatonin und verbessern Schlafqualität signifikant.",
          source: "Burkhart & Phelps, Chronobiology International, 2009",
          dose: "Klare Computer-Brillen: nur für Augenstrain, kaum Schlaf-Benefit. Amber/Orange-Gläser: ab 2 Std. vor dem Schlafen für echten Melatonin-Schutz. Zum Beispiel eine Brille mit Blaulichtfilter in Amber- oder Orange-Tönung.",
        },
      ],
    },

    // ── ROTLICHT-THERAPIE ────────────────────
    {
      id: "light_red_light",
      label: "Rotlicht-Therapie (PBM)",
      icon: "🔴",
      habits: [
        {
          id: "hab_light_red_what",
          label: "Rotlicht: Photobiomodulation Grundlagen",
          time: "flexible",
          icon: "🔴",
          why: "Bei der Photobiomodulation wird rotes und nahinfrarotes Licht auf die Haut gestrahlt. Die Forschung dazu läuft noch, die Ergebnisse sind uneinheitlich.",
          source: "Hamblin, AIMS Biophysics, 2017 · de Freitas & Hamblin, IEEE Journal of Selected Topics in Quantum Electronics, 2016",
          dose: "Laut Herstellerangabe des Geräts, mit Schutzbrille. Bei lichtsensibilisierenden Medikamenten, Hautkrebs-Vorgeschichte oder Schwangerschaft vorher ärztlich abklären.",
        },
        {
          id: "hab_light_red_skin",
          label: "Rotlicht für Haut & Kollagen",
          time: "flexible",
          icon: "✨",
          why: "Rotlicht-Geräte für das Gesicht werden in der Hautpflege beliebter. Einige kleine Studien deuten auf Effekte auf das Hautbild hin, die Datenlage ist aber begrenzt.",
          source: "Wunsch & Matuschka, Photomedicine and Laser Surgery, 2014",
          dose: "Laut Herstellerangabe des Geräts, mit Schutzbrille. Bei auffälligen Hautveränderungen vorher ärztlich abklären.",
        },
      ],
    },

    // ── SAISONALE RHYTHMEN ───────────────────
    {
      id: "light_seasonal",
      label: "Saisonale Licht-Anpassung",
      icon: "🍂",
      habits: [
        {
          id: "hab_light_sad_winter",
          label: "Tageslichtlampe in der dunklen Jahreszeit",
          time: "morning",
          icon: "❄️",
          why: "Im Winter ist das Tageslicht deutlich schwächer. Viele nutzen morgens eine Tageslichtlampe. Bei anhaltender Niedergeschlagenheit bitte ärztliche Hilfe suchen.",
          source: "Golden et al., Annals of Internal Medicine, 2005",
          dose: "Oktober–März: 10.000 Lux Tageslichtlampe (zertifiziert, UV-gefiltert), 20–30 Min. morgens während des Frühstücks oder Kaffees. Lampe in 30–50cm Abstand, leicht von oben. Keine direkte Blickrichtung nötig.",
        },
        {
          id: "hab_light_darkness_winter",
          label: "Mehr Dunkelheit im Winter = besserer Schlaf",
          time: "night",
          icon: "🌑",
          why: "Im Winter sind die Tage kürzer, und viele haben ein größeres Schlafbedürfnis. Warmes, gedimmtes Licht am Abend kann beim Abschalten helfen.",
          source: "Wehr et al., American Journal of Physiology, 1993",
          dose: "Oktober–März: Abends ab 20 Uhr nur warmes Licht (<2700K), früher ins Bett (23 Uhr statt Mitternacht), morgens Tageslichtlampe. Saisonal angepasster Schlafrhythmus.",
        },
      ],
    },
  ],
};